












































import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator';
import draggable from 'vuedraggable';
import { shuffle } from 'lodash';
import { SerializedSorting } from '@xlearn/common';
import { Container, inject, injectable } from 'inversify';
import { PresenterService, PresenterServiceSymbol } from '@/app/presenter/presenter-service';
import * as Api from "@/app/question/components/api";
import { SortingAnswer, SortingState } from "./sorting-interfaces";
import { TranslateResult } from 'vue-i18n';
import i18n from '@/plugins/i18n';

interface QuestionScore {
  correct: number;
  incorrect: number;
}

interface Item {
  id: string;
  title: string;
  selected: boolean;
}

export interface AnswerItem {
  id: string;
  answer: string;
  score: QuestionScore;
}

export type SortingCorrectAnswer = AnswerItem[]

@Component({
  components: {
    draggable
  },
})
export default class SortingQuestionComponent extends Vue implements Api.QuestionComponent<SerializedSorting, SortingAnswer, SortingState, SortingCorrectAnswer> {
  @Prop({
    required: true,
  })
  public question: Api.Question<SerializedSorting>;

  @Prop({
    required: true,
  })
  public evaluated: boolean;

  @Prop({
    required: true,
  })
  public answer: Api.Answer<SortingAnswer> | null;

  @Prop({
    required: true,
  })
  public showSolution: boolean;

  @Prop({
    required: true,
  })
  public state: Api.State<SortingState> | null;

  private items: Item[] = [];

  private selectMode: boolean = false;

  private selected: boolean = false;

  private selectedItemIndex: number = -1;
  private dragging: boolean = false;

  public solutionProps() {
    return {
      userAnswer: {
        answerIdsOrder: this.answer ? this.answer.answer.answerIdsOrder : [],
      },
      correctAnswer: this.question.content.answers,
    };
  };

  private mounted() {
    this.items = this.initialItems();
  }

  private get idsInCorrectOrder(): string[] {
    return this.question.content.answers.map((answer) => answer.id);
  }

  private onDrag(event: DragEvent, index: number): void {
    this.selectMode = false;
    this.dragging = true;
    this.selectedItemIndex = index;
    this.items.forEach((item: Item, i: number) => {
      item.selected = i === index
    });
    this.removeGhostImage(event);
  }

  private onDrop(): void {
    this.emitAnswerChanged();
    this.dragging = false;
    this.items.forEach((item: Item, i: number) => {
      item.selected = false
    });
  }

  private selectItem(item: { id: string; title: string; selected: boolean; }): void {
    if (this.evaluated) {
      return;
    }

    if (item.selected && this.selectMode) {
      this.selectMode = false;
      item.selected = false;

      return;
    }

    if (!this.selectMode) {
      this.selectMode = true;
      item.selected = true;
      this.items.forEach((element: Item, i: number) => {
        if (element.selected) {
          this.selectedItemIndex = i;
        }

        if (element !== item) element.selected = false;
      });
    }

    if (!item.selected && this.selectMode) {
      const selectedItem = this.items.find((element: Item) => element.selected);
      const selectedIndex: number = this.items.findIndex((obj: Item) => obj === selectedItem);
      const targetIndex: number = this.items.findIndex((obj: Item) => obj === item);

      [this.items[selectedIndex], this.items[targetIndex]] = [this.items[targetIndex], this.items[selectedIndex]];

      this.items[selectedIndex].selected = false;
      this.items[targetIndex].selected = false;
      this.selectMode = false;
      this.selectedItemIndex = -1;
      this.dragging = false;

      this.emitAnswerChanged();
    }
  }

  private isInCorrectPlace(id: string): boolean {
    const currentIndex: number = this.items.findIndex((item) => item.id === id);
    const correctIndex: number = this.question.content.answers.findIndex((answer) => answer.id === id);
    return currentIndex === correctIndex;
  }

  private listOrder(index: number): number {
    return index + 1;
  }

  @Watch('evaluated')
  private evaluatedChanged(newValue: boolean): void {
    if (!newValue) {
      this.items = this.initialItems();
    }
  }

  @Emit('answer-changed')
  public emitAnswerChanged(): Api.AnswerChangedPayload<SortingAnswer> {
    const answerIdsOrder: string[] = this.items.map((item) => item.id);

    return {
      answer: {
        answerIdsOrder,
      },
      score: this.calculateScore()
    };
  }

  @Emit('state-changed')
  public emitStateChanged(state: Api.StateChangedPayload<SortingState>): Api.StateChangedPayload<SortingState> {
    return state;
  }

  private removeGhostImage(event: DragEvent): void {
    const emptyDiv = document.createElement('div');
    if (event.dataTransfer !== null) {
      event.dataTransfer.setDragImage(emptyDiv, 0, 0);
    }
  }

  private calculateScore(): number {
    let score: number = 0;
    this.items.forEach((item) => {
      const isInCorrectPlace: boolean = this.isInCorrectPlace(item.id);
      const answer = this.question.content.answers.find((a) => a.id === item.id);
      if (answer) {
        const answerScore: QuestionScore = answer.score as QuestionScore;
        score += isInCorrectPlace ? answerScore.correct : answerScore.incorrect;
      }
    });

    return score;
  }

  private initialItems(): Item[] {
    let items: Item[] = [];

    shuffle(this.question.content.answers).forEach((answer) => {
      items.push({
        id: answer.id,
        title: answer.answer,
        selected: false
      });
    });

    let ordering: string[] = [];

    if (this.answer) {
      ordering = this.answer.answer.answerIdsOrder;
    } else if (this.state) {
      ordering = this.state.state.answersOrder;
    }

    if (ordering.length) {
      items.sort((a: Item, b: Item) =>
        ordering.indexOf(a.id) - ordering.indexOf(b.id)
      );
    }

    if (!this.state) {
      this.emitStateChanged({
        state: {
          answersOrder: items.map(i => i.id),
        }
      });
    }

    return items;
  }

  private moveItemUp(e: KeyboardEvent, itemIndex: number) {
    if (!this.dragging || this.evaluated) {
      return;
    }

    const newIndex = itemIndex - 1;

    if (newIndex >= 0) {
      const item = this.items.splice(itemIndex, 1)[0];
      const closestLiParent = this.closestEl(<HTMLElement>e.target, (el: HTMLElement) => {
        return el.tagName.toLowerCase() === 'ul';
      });

      this.items.splice(newIndex, 0, item);
      if (closestLiParent) {
        const linkEl = closestLiParent.querySelector<HTMLElement>(`li:nth-child(${newIndex + 1}) a`)

        if (linkEl) {
          linkEl.focus();
        }
      }

      this.selectedItemIndex = newIndex;
    }

    this.emitAnswerChanged();
  }

  private moveItemDown(e: KeyboardEvent, itemIndex: number) {
    if (!this.dragging || this.evaluated) {
      return;
    }

    const newIndex = itemIndex + 1;

    if (newIndex < this.items.length) {
      const item = this.items.splice(itemIndex, 1)[0];
      const closestLiParent = this.closestEl(<HTMLElement>e.target, (el: HTMLElement) => {
        return el.tagName.toLowerCase() === 'ul';
      });

      this.items.splice(newIndex, 0, item);

      if (closestLiParent) {
        const linkEl = closestLiParent.querySelector<HTMLElement>(`li:nth-child(${newIndex + 1}) a`);

        if (linkEl) {
          linkEl.focus();
        }
      }

      this.selectedItemIndex = newIndex;
    }

    this.emitAnswerChanged();
  }

  private dragNDropItem(e: MouseEvent, index: number) {
    if (this.evaluated) return;

    if (!this.dragging) {
      this.dragging = true;
      this.selectedItemIndex = index;
    } else {
      this.dragging = false;
    }
  }

  private preventIfDragging(e: KeyboardEvent) {
    if (this.dragging) {
      e.preventDefault();
    }
  }

  private closestEl(el: HTMLElement | null, fn: Function): HTMLElement | null {
    return el && (fn(el) ? el : this.closestEl(<HTMLElement>el.parentNode, fn));
  }

  private t(key: string, params: any = {}): TranslateResult {
    return i18n.t(key, params);
  }
}
